import { MathUtils, Triangle, TypedArray, Vector3 } from 'three';

export type NumberArrayLike = ArrayLike<number> | TypedArray;

const _v0 = new Vector3();
const _v1 = new Vector3();
const _normal = new Vector3();
const _triangle = new Triangle();

/**
 * Compute edges for a given positions array and indexes array if provided
 *
 * @see https://github.com/mrdoob/three.js/blob/master/src/geometries/EdgesGeometry.js
 */
export const computeEdges = (
  positions: NumberArrayLike,
  indexes?: NumberArrayLike,
  thresholdAngle = 0.5
) => {
  const precisionPoints = 4;
  const precision = Math.pow(10, precisionPoints);
  const thresholdDot = Math.cos(MathUtils.DEG2RAD * thresholdAngle);

  const indexAttr = indexes;
  const positionAttr = positions;
  const indexCount = indexAttr ? indexAttr.length : positionAttr.length / 3;

  const edgeData: Record<string, { index0: number; index1: number; normal: Vector3 } | null> = {};
  const vertices0: Vector3[] = [];
  const vertices1: Vector3[] = [];

  for (let i = 0; i < indexCount; i += 3) {
    const indexArr = indexAttr
      ? [indexAttr[i], indexAttr[i + 1], indexAttr[i + 2]]
      : [i, i + 1, i + 2];

    _triangle.a.set(
      positionAttr[indexArr[0] * 3],
      positionAttr[indexArr[0] * 3 + 1],
      positionAttr[indexArr[0] * 3 + 2]
    );
    _triangle.b.set(
      positionAttr[indexArr[1] * 3],
      positionAttr[indexArr[1] * 3 + 1],
      positionAttr[indexArr[1] * 3 + 2]
    );
    _triangle.c.set(
      positionAttr[indexArr[2] * 3],
      positionAttr[indexArr[2] * 3 + 1],
      positionAttr[indexArr[2] * 3 + 2]
    );

    _triangle.getNormal(_normal);

    const hashes = [
      `${Math.round(_triangle.a.x * precision)},${Math.round(
        _triangle.a.y * precision
      )},${Math.round(_triangle.a.z * precision)}`,
      `${Math.round(_triangle.b.x * precision)},${Math.round(
        _triangle.b.y * precision
      )},${Math.round(_triangle.b.z * precision)}`,
      `${Math.round(_triangle.c.x * precision)},${Math.round(
        _triangle.c.y * precision
      )},${Math.round(_triangle.c.z * precision)}`,
    ];

    if (hashes[0] === hashes[1] || hashes[1] === hashes[2] || hashes[2] === hashes[0]) continue;

    for (let j = 0; j < 3; j++) {
      const jNext = (j + 1) % 3;
      const vertKeys = ['a', 'b', 'c'] as const;
      const v0 = _triangle[vertKeys[j]];
      const v1 = _triangle[vertKeys[jNext]];

      const hash = `${hashes[j]}_${hashes[jNext]}`;
      const reverseHash = `${hashes[jNext]}_${hashes[j]}`;

      if (edgeData[reverseHash]) {
        if (edgeData[reverseHash] && _normal.dot(edgeData[reverseHash]!.normal) <= thresholdDot) {
          vertices0.push(v0.clone());
          vertices1.push(v1.clone());
        }
        edgeData[reverseHash] = null;
      } else if (!edgeData[hash]) {
        edgeData[hash] = {
          index0: indexArr[j],
          index1: indexArr[jNext],
          normal: _normal.clone(),
        };
      }
    }
  }

  for (const key in edgeData) {
    if (edgeData[key]) {
      const { index0, index1 } = edgeData[key]!;
      _v0.set(positionAttr[index0 * 3], positionAttr[index0 * 3 + 1], positionAttr[index0 * 3 + 2]);
      _v1.set(positionAttr[index1 * 3], positionAttr[index1 * 3 + 1], positionAttr[index1 * 3 + 2]);

      vertices0.push(_v0.clone());
      vertices1.push(_v1.clone());
    }
  }

  return vertices0.map((v, i) => [v, vertices1[i]] as [Vector3, Vector3]);
};
